import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { type ReactNode, useEffect, useState } from "react";

import { BankAvatar, BankLogo } from "@dfp/react-components";

import type { BankFlowProps } from "./bankFlow.types.ts";

// Replica of UniCredit Bank's PSD2 "Open Banking: souhlas" pages. The coarse
// BankFlowStep (login/sca/accounts) can't express UniCredit's several login
// sub-screens, so this flow drives itself with a richer LOCAL stage union and
// maps initialStep="accounts" straight to the final consent screen for the
// guided tour. Copy is intentionally hard-coded — these are external bank
// pages, not localized app UI.
const UNI_RED = "#E2001A";
const UNI_TEAL = "#007E8C";
const UNI_TEAL_DARK = "#00636E";
const UNI_TEXT = "#2B2B2B";
const UNI_MUTED = "#6B6B6B";
const UNI_BG_TOP = "#cfe6f5";
const UNI_BG_BOTTOM = "#eaf4fb";

const FONT_STACK = '"Helvetica Neue", Helvetica, Arial, sans-serif';

/** Local login progression — finer-grained than the coarse BankFlowStep. */
type UnicreditStage = "channel" | "username" | "birth" | "smartkey" | "consent";

const tealButtonSx = {
  bgcolor: UNI_TEAL,
  color: "#fff",
  borderRadius: 0.5,
  fontFamily: FONT_STACK,
  fontWeight: 700,
  letterSpacing: "0.04em",
  boxShadow: "none",
  "&:hover": { bgcolor: UNI_TEAL_DARK, boxShadow: "none" },
};

const tealLinkSx = {
  color: UNI_TEAL,
  fontWeight: 700,
  letterSpacing: "0.03em",
  fontFamily: FONT_STACK,
  cursor: "pointer",
  textDecoration: "none",
  "&:hover": { textDecoration: "underline" },
};

/** Black header bar shared by the auth card stages (username/birth/smartkey). */
function UnicreditCardHeader() {
  return (
    <Stack
      direction="row"
      sx={{
        alignItems: "center",
        justifyContent: "space-between",
        bgcolor: "#000",
        px: 2,
        py: 1.25,
      }}
    >
      <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
        <Typography sx={{ color: "#fff", fontSize: 18 }}>⊘</Typography>
        <Typography
          sx={{
            color: "#fff",
            fontWeight: 700,
            fontSize: 16,
            fontFamily: FONT_STACK,
          }}
        >
          UniCredit
        </Typography>
      </Stack>
      <Typography
        sx={{ color: "#fff", fontSize: 13, fontFamily: FONT_STACK }}
      >
        ENGLISH ▾
      </Typography>
    </Stack>
  );
}

/** Centered ~360px auth card on the light-blue gradient background. */
function UnicreditAuthCard({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(180deg, ${UNI_BG_TOP} 0%, ${UNI_BG_BOTTOM} 100%)`,
        fontFamily: FONT_STACK,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        py: 8,
      }}
    >
      <Box
        sx={{
          width: 360,
          maxWidth: "92vw",
          bgcolor: "#fff",
          borderRadius: 1,
          overflow: "hidden",
          boxShadow: "0 8px 30px rgba(0,0,0,0.18)",
        }}
      >
        <UnicreditCardHeader />
        <Box sx={{ p: 3 }}>{children}</Box>
      </Box>
    </Box>
  );
}

const authInputSx = {
  width: "100%",
  bgcolor: "#fff",
  "& .MuiOutlinedInput-root": { borderRadius: 0.5 },
  "& input": { py: 1.1, px: 1.25, fontSize: 14, fontFamily: FONT_STACK },
};

/** UniCredit Bank PSD2 flow: channel pick → login → SCA Smart Key → consent. */
export function UnicreditFlow({
  thirdPartyName,
  ownerName,
  username,
  connectionId,
  accounts,
  initialStep,
  onConnect,
  onCancel,
}: BankFlowProps) {
  const [stage, setStage] = useState<UnicreditStage>(
    initialStep === "accounts" ? "consent" : "channel",
  );
  const [loginName, setLoginName] = useState(username);
  const [birthDay, setBirthDay] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(accounts.map((account) => account.id)),
  );

  // The Smart Key push approval "arrives" by itself after a moment, like the
  // real mobile-app confirmation would.
  useEffect(() => {
    if (stage !== "smartkey") return;
    const timer = setTimeout(() => setStage("consent"), 3000);
    return () => clearTimeout(timer);
  }, [stage]);

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── CHANNEL ──────────────────────────────────────────────────────────────
  if (stage === "channel") {
    const channels = [
      {
        icon: "💻",
        title: "Online Banking",
        helper: "Pokud jste na počítači, klikněte zde:",
        link: "ONLINE BANKING ›",
        onClick: () => setStage("username"),
      },
      {
        icon: "📱",
        title: "Smart Banking",
        helper: "Pokud jste na mobilu, klikněte zde:",
        link: "SMART BANKING ›",
        onClick: () => setStage("username"),
      },
      {
        icon: "🏪",
        title: "Corporate",
        helper: "Pokud jste korporátní klient, klikněte zde:",
        link: "CORPORATE ›",
        onClick: () => setStage("username"),
      },
    ];

    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#fff", fontFamily: FONT_STACK }}>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            px: 4,
            py: 1.5,
            borderBottom: "1px solid #e6e6e6",
          }}
        >
          <Stack direction="row" sx={{ alignItems: "center", gap: 3 }}>
            <Stack direction="row" sx={{ alignItems: "center", gap: 1.25 }}>
              <BankLogo
                bank="UNICREDIT"
                style={{ height: 24, width: "auto", display: "block" }}
              />
            </Stack>
            <Typography
              sx={{
                color: UNI_TEXT,
                fontSize: 12.5,
                fontWeight: 700,
                letterSpacing: "0.03em",
                fontFamily: FONT_STACK,
                display: { xs: "none", lg: "block" },
              }}
            >
              ÚČTY A KARTY&nbsp;&nbsp;&nbsp;PŮJČKY A HYPOTÉKY&nbsp;&nbsp;&nbsp;SPOŘENÍ
              A INVESTICE&nbsp;&nbsp;&nbsp;POJIŠTĚNÍ
            </Typography>
          </Stack>
          <Typography
            sx={{
              color: UNI_TEXT,
              fontSize: 12.5,
              fontWeight: 700,
              letterSpacing: "0.03em",
              fontFamily: FONT_STACK,
            }}
          >
            🔍 HLEDAT&nbsp;&nbsp;&nbsp;↪ PŘIHLÁSIT
          </Typography>
        </Stack>

        <Container maxWidth="md" sx={{ pt: 6, pb: 8, textAlign: "center" }}>
          <Typography
            variant="h2"
            sx={{
              color: UNI_TEXT,
              fontWeight: 700,
              fontSize: 32,
              fontFamily: FONT_STACK,
              mb: 1.5,
            }}
          >
            Open Banking: souhlas
          </Typography>
          <Typography
            sx={{
              color: UNI_MUTED,
              fontSize: 13,
              letterSpacing: "0.04em",
              fontFamily: FONT_STACK,
              mb: 6,
            }}
          >
            NÍŽE SI VYBERTE, POMOCÍ KTERÉHO KANÁLU CHCETE POTVRDIT SVŮJ SOUHLAS
            PRO TŘETÍ STRANU
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
              gap: 5,
            }}
          >
            {channels.map((channel) => (
              <Stack
                key={channel.title}
                sx={{ alignItems: "center", gap: 1.25, px: 1 }}
              >
                <Typography sx={{ fontSize: 44 }}>{channel.icon}</Typography>
                <Typography
                  sx={{
                    color: UNI_TEXT,
                    fontWeight: 700,
                    fontSize: 18,
                    fontFamily: FONT_STACK,
                  }}
                >
                  {channel.title}
                </Typography>
                <Typography
                  sx={{
                    color: UNI_MUTED,
                    fontSize: 13.5,
                    fontFamily: FONT_STACK,
                  }}
                >
                  {channel.helper}
                </Typography>
                <Link onClick={channel.onClick} sx={{ ...tealLinkSx, mt: 0.5 }}>
                  {channel.link}
                </Link>
              </Stack>
            ))}
          </Box>
        </Container>
      </Box>
    );
  }

  // ── USERNAME ─────────────────────────────────────────────────────────────
  if (stage === "username") {
    return (
      <UnicreditAuthCard>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 20,
            color: UNI_TEXT,
            fontFamily: FONT_STACK,
            mb: 2.5,
          }}
        >
          Welcome!
        </Typography>

        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: UNI_TEXT,
            fontFamily: FONT_STACK,
            mb: 0.75,
          }}
        >
          Username
        </Typography>
        <TextField
          placeholder="Enter Username"
          value={loginName}
          onChange={(event) => setLoginName(event.target.value)}
          sx={authInputSx}
        />

        <Button
          fullWidth
          variant="contained"
          onClick={() => setStage("birth")}
          disabled={!loginName}
          sx={{ ...tealButtonSx, mt: 2.5 }}
        >
          NEXT
        </Button>

        <Typography
          sx={{
            fontSize: 11.5,
            color: UNI_MUTED,
            fontFamily: FONT_STACK,
            mt: 2.5,
            lineHeight: 1.5,
          }}
        >
          ⓘ You can find the Username according to the instructions{" "}
          <Box component="span" sx={{ color: UNI_TEAL, fontWeight: 700 }}>
            HERE
          </Box>
          . This application uses technical cookies necessary for its
          functioning.
        </Typography>
      </UnicreditAuthCard>
    );
  }

  // ── BIRTH ────────────────────────────────────────────────────────────────
  if (stage === "birth") {
    return (
      <UnicreditAuthCard>
        <Stack sx={{ alignItems: "center", gap: 1, mb: 2.5 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              bgcolor: "#d9d9d9",
            }}
          />
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 16,
              color: UNI_TEXT,
              fontFamily: FONT_STACK,
            }}
          >
            {username}
          </Typography>
          <Link onClick={() => setStage("username")} sx={tealLinkSx}>
            ENTER AGAIN
          </Link>
        </Stack>

        <Typography
          sx={{
            fontSize: 13.5,
            color: UNI_TEXT,
            fontFamily: FONT_STACK,
            mb: 1.5,
          }}
        >
          Please, fill the day and month of birth
        </Typography>

        <Stack direction="row" sx={{ gap: 1.5, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: UNI_TEXT,
                fontFamily: FONT_STACK,
                mb: 0.5,
              }}
            >
              Day
            </Typography>
            <TextField
              value={birthDay}
              onChange={(event) => setBirthDay(event.target.value)}
              sx={authInputSx}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: UNI_TEXT,
                fontFamily: FONT_STACK,
                mb: 0.5,
              }}
            >
              Month
            </Typography>
            <Select
              displayEmpty
              value={birthMonth}
              onChange={(event) => setBirthMonth(event.target.value)}
              sx={{
                width: "100%",
                fontSize: 14,
                fontFamily: FONT_STACK,
                "& .MuiOutlinedInput-input": { py: 1.1, px: 1.25 },
              }}
            >
              <MenuItem value="">—</MenuItem>
              {Array.from({ length: 12 }, (_, index) => index + 1).map(
                (month) => (
                  <MenuItem key={month} value={String(month)}>
                    {month}
                  </MenuItem>
                ),
              )}
            </Select>
          </Box>
        </Stack>

        <Stack
          direction="row"
          onClick={() => setRememberMe((prev) => !prev)}
          sx={{ alignItems: "center", gap: 0.5, cursor: "pointer", mb: 0.5 }}
        >
          <Checkbox
            size="small"
            checked={rememberMe}
            sx={{ p: 0, color: UNI_TEAL, "&.Mui-checked": { color: UNI_TEAL } }}
          />
          <Typography
            sx={{ fontSize: 13.5, color: UNI_TEXT, fontFamily: FONT_STACK }}
          >
            Remember me
          </Typography>
        </Stack>

        <Button
          fullWidth
          variant="contained"
          onClick={() => setStage("smartkey")}
          sx={{ ...tealButtonSx, mt: 2 }}
        >
          LOGIN
        </Button>
      </UnicreditAuthCard>
    );
  }

  // ── SMARTKEY ─────────────────────────────────────────────────────────────
  if (stage === "smartkey") {
    return (
      <UnicreditAuthCard>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 16,
            color: UNI_TEXT,
            fontFamily: FONT_STACK,
            textAlign: "center",
            mb: 2.5,
          }}
        >
          Please confirm the login in Smart Banking application part Smart Key
        </Typography>

        <Box
          sx={{
            bgcolor: UNI_RED,
            borderRadius: 1,
            py: 1.5,
            textAlign: "center",
            mb: 2,
          }}
        >
          <Typography
            sx={{
              color: "#fff",
              fontWeight: 700,
              fontSize: 15,
              fontFamily: FONT_STACK,
            }}
          >
            Czech Republic
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize: 13,
            color: UNI_MUTED,
            fontFamily: FONT_STACK,
            textAlign: "center",
            mb: 2,
          }}
        >
          Date and time 10.06.2026 11:38:50
        </Typography>

        <Stack sx={{ alignItems: "center", mb: 2 }}>
          <CircularProgress size={32} sx={{ color: UNI_TEAL }} />
        </Stack>

        <Typography
          sx={{
            fontSize: 12.5,
            color: UNI_MUTED,
            fontFamily: FONT_STACK,
            textAlign: "center",
            mb: 1.5,
          }}
        >
          In case push notification was not received, please use offline option
          to login:
        </Typography>

        <Stack sx={{ alignItems: "center", gap: 1.5 }}>
          <Link sx={{ ...tealLinkSx, textAlign: "center", fontSize: 13 }}>
            USE SMART KEY OFFLINE (SCAN QR CODE BY PHONE)
          </Link>
          <Link onClick={onCancel} sx={tealLinkSx}>
            CANCEL
          </Link>
        </Stack>
      </UnicreditAuthCard>
    );
  }

  // ── CONSENT ──────────────────────────────────────────────────────────────
  const consentRows: { label: string; value: ReactNode }[] = [
    { label: "ID souhlasu", value: connectionId },
    { label: "Status", value: "Obdržený" },
    { label: "Platnost", value: "07.12.2026" },
    { label: "Jméno třetí strany", value: thirdPartyName },
    { label: "Registrační číslo", value: "10669205" },
    { label: "Země registrace", value: "CZ" },
    { label: "Státní příslušný orgán třetí strany", value: "ČNB" },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "rgba(20,40,60,0.55)",
        fontFamily: FONT_STACK,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        py: 6,
      }}
    >
      <Box
        sx={{
          width: 560,
          maxWidth: "94vw",
          bgcolor: "#fff",
          borderRadius: 1,
          boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            pt: 2.5,
            pb: 1.5,
          }}
        >
          <Stack direction="row" sx={{ alignItems: "center", gap: 1.25 }}>
            <BankAvatar bank="UNICREDIT" size={32} />
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 18,
                color: UNI_TEXT,
                fontFamily: FONT_STACK,
              }}
            >
              Souhlas k informacím o účtu
            </Typography>
          </Stack>
          <IconButton aria-label="Zavřít" onClick={onCancel} sx={{ p: 0.5 }}>
            <Typography sx={{ fontSize: 20, color: UNI_MUTED }}>✕</Typography>
          </IconButton>
        </Stack>

        <Divider />

        <Box sx={{ p: 3 }}>
          <Stack sx={{ gap: 1.25, mb: 2.5 }}>
            {consentRows.map((row) => (
              <Stack key={row.label} direction="row" sx={{ gap: 2 }}>
                <Typography
                  sx={{
                    width: 220,
                    flexShrink: 0,
                    fontSize: 13,
                    color: UNI_MUTED,
                    fontFamily: FONT_STACK,
                  }}
                >
                  {row.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: UNI_TEXT,
                    fontFamily: FONT_STACK,
                    wordBreak: "break-word",
                  }}
                >
                  {row.value}
                </Typography>
              </Stack>
            ))}
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 14,
              color: UNI_TEXT,
              fontFamily: FONT_STACK,
              mb: 1.5,
            }}
          >
            Souhlasy a účty
          </Typography>

          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: UNI_MUTED,
              letterSpacing: "0.03em",
              fontFamily: FONT_STACK,
              mb: 1,
            }}
          >
            Detaily účtu
          </Typography>

          <Box
            data-tour="bank-accounts"
            sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2.5 }}
          >
            {accounts.map((account) => (
              <Stack
                key={account.id}
                direction="row"
                onClick={() => toggle(account.id)}
                sx={{ alignItems: "center", gap: 1, cursor: "pointer" }}
              >
                <Checkbox
                  size="small"
                  checked={selectedIds.has(account.id)}
                  sx={{
                    p: 0,
                    color: UNI_TEAL,
                    "&.Mui-checked": { color: UNI_TEAL },
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 13.5,
                    color: UNI_TEXT,
                    fontFamily: FONT_STACK,
                  }}
                >
                  {ownerName} | {account.accountNumber} | {account.currency}
                </Typography>
              </Stack>
            ))}
          </Box>

          <Typography
            sx={{
              fontSize: 11.5,
              color: UNI_MUTED,
              fontFamily: FONT_STACK,
              lineHeight: 1.5,
              mb: 2.5,
            }}
          >
            Podpisem tohoto formuláře se odstraní existující souhlas tohoto typu
            udělený stejné aplikaci třetí strany, pokud existuje.
          </Typography>

          <Button
            data-tour="bank-connect-submit"
            fullWidth
            variant="contained"
            disabled={selectedIds.size === 0}
            onClick={() =>
              onConnect(
                accounts.filter((account) => selectedIds.has(account.id)),
              )
            }
            sx={tealButtonSx}
          >
            Uložit
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
